import ic_arrow from "@/assets/images/icons/arrow-dropdown.svg";
import { usePathname, useRouter } from "@/i18n/routing";
import { motion } from "framer-motion";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";

const MotionImage = motion.create(Image);

interface CustomDropdownProps {
  isBigArrow?: boolean;
  onChange: (value: string) => void;
  options: Option[];
  readonly?: boolean;
  value?: string;
}

interface Option {
  disabled?: boolean;
  img?: string;
  label: string;
  link?: string;
  value: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({ isBigArrow = false, onChange, options, readonly, value }) => {
  const router = useRouter();
  const currentPath = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption] = useState<null | Option>(options.find((option) => option.value === value) || null);
  const [hoveredItem, setHoveredItem] = useState<null | string>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleOptionClick = (option: Option) => {
    if (option.disabled) {
      return;
    }
    onChange(option.value);
    setIsOpen(false);

    if (option.link) {
      const [path, hash] = option.link.split("#");

      if (currentPath === path) {
        const targetElement = document.getElementById(hash);
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      } else {
        router.push(option.link);
        const timer = setTimeout(() => {
          const targetElement = document.getElementById(hash);
          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 300);

        return () => clearTimeout(timer);
      }
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <motion.button
        className={`flex w-full items-center font-raleway text-base font-medium leading-[1] text-primary dark:text-white ${readonly ? "cursor-default" : "cursor-pointer"} text-primary ${isBigArrow ? "gap-3" : ""} rounded-lg tracking-[0] transition-colors md:h-11 md:py-0 md:text-xl lg:text-sm lg:leading-[73px] lg:tracking-normal xl:text-base xl:leading-[73px] xl:tracking-normal`}
        disabled={readonly}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedOption ? selectedOption.label : value}

        <MotionImage
          alt="select language vicekeeper"
          animate={{ rotate: isOpen ? 180 : 0 }}
          className={`pt-[1px] ${isBigArrow ? "w-6" : ""} dark:contrast-1000 dark:brightness-0 dark:invert`}
          src={ic_arrow}
        />
      </motion.button>

      {isOpen && (
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="menucontainer absolute left-0 z-[1000] w-[160px] overflow-hidden rounded bg-[#fbfbfb] p-1 text-sm shadow-md lg:text-sm xl:text-base"
          exit={{ opacity: 0, y: -10 }}
          initial={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {options.map((option) => (
            <motion.div
              className={`z-50 flex w-auto items-center px-4 py-2 ${
                option.disabled ? "cursor-default opacity-50" : "cursor-pointer"
              } ${selectedOption?.value === option.value ? "rounded bg-[#1F2951]" : "bg-[#fbfbfb]"} ${
                hoveredItem === option.value && selectedOption?.value !== option.value && !option.disabled
                  ? "rounded bg-yellow"
                  : "bg-[#fbfbfb]"
              } duration-200`}
              key={option.value}
              onClick={() => handleOptionClick(option)}
              onHoverEnd={() => !option.disabled && setHoveredItem(null)}
              onHoverStart={() => !option.disabled && setHoveredItem(option.value)}
            >
              {option.label}
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default CustomDropdown;
